import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { toast } from "sonner";
import { BookOpen, Lock, Globe, Trash2, Search, Plus, Sparkles, Link2, FileUp, Loader2, RefreshCw } from "lucide-react";

const CATS = ["service", "case_study", "differentiator", "proof_point", "methodology"];

function ConfBadge({ c }) {
  const internal = c === "internal";
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase px-1.5 py-0.5 border"
      style={{ color: internal ? "#B23A2E" : "#2F7A3E", borderColor: internal ? "#B23A2E" : "#2F7A3E" }}>
      {internal ? <Lock className="w-2.5 h-2.5" /> : <Globe className="w-2.5 h-2.5" />}{internal ? "internal-only" : "public-safe"}
    </span>
  );
}

export default function KnowledgeBase() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [entries, setEntries] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "service", confidentiality: "public", tags: "" });
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [ing, setIng] = useState({ url: "", category: "service", confidentiality: "public" });
  const [ingBusy, setIngBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  const load = () => api.get("/kb").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const ingestUrl = async () => {
    if (!/^https?:\/\//i.test(ing.url.trim())) { toast.error("Enter a full URL starting with http:// or https://"); return; }
    setIngBusy(true);
    try {
      const r = await api.post("/kb/ingest/url", { url: ing.url.trim(), category: ing.category, confidentiality: ing.confidentiality });
      toast.success(`Learned from URL · ${r.data.chars_extracted.toLocaleString()} chars${r.data.summarized ? " + AI summary" : ""}`);
      setIng({ ...ing, url: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setIngBusy(false); }
  };

  const ingestFile = async (file) => {
    if (!file) return;
    const okExt = /\.(pdf|docx|pptx)$/i.test(file.name);
    if (!okExt) { toast.error("Supported: PDF, DOCX, PPTX"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File is over the 15 MB limit"); return; }
    setIngBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", ing.category);
      fd.append("confidentiality", ing.confidentiality);
      const r = await api.post("/kb/ingest/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Learned from ${file.name} · ${r.data.chars_extracted.toLocaleString()} chars${r.data.summarized ? " + AI summary" : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setIngBusy(false); }
  };

  const refreshEntry = async (id) => {
    setRefreshing(id);
    try {
      const r = await api.post(`/kb/${id}/refresh`, {});
      toast.success(`Re-fetched · ${r.data.chars_extracted.toLocaleString()} chars${r.data.summarized ? " + AI summary" : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRefreshing(null); }
  };

  const create = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content required"); return; }
    try {
      await api.post("/kb", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Entry added");
      setForm({ title: "", content: "", category: "service", confidentiality: "public", tags: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/kb/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const search = async () => {
    if (!q.trim()) { setResults(null); toast.info("Enter a search term first"); return; }
    try { const r = await api.post("/kb/search", { query: q, external: false, k: 5 }); setResults(r.data); }
    catch { toast.error("Search failed"); }
  };

  return (
    <>
      <PageHeader eyebrow="RAG · GROUNDS EVERY DRAFTING AGENT" title="VIS Knowledge Base" hint="Upload approved decks, case studies and pricing notes. Every AI draft, brief and proposal is grounded in what's here — the better this is, the better the copy."
        right={<span className="flex items-center gap-1.5 font-mono text-xs text-[#1C2530]/50"><BookOpen className="w-3.5 h-3.5" /> {entries?.length ?? 0} ENTRIES</span>} />
      <div className="p-8 space-y-6">
        <div className="border border-[#DFDAD0] bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Retrieval Test (what agents pull)</div>
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="kb-search-input"
              placeholder="e.g. EHS safety training for manufacturing" className="flex-1 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
            <button onClick={search} data-testid="kb-search-btn" className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]"><Search className="w-3.5 h-3.5" /> Search</button>
          </div>
          {results && (
            <div className="mt-3 space-y-2" data-testid="kb-search-results">
              {results.length === 0 ? <div className="font-mono text-xs text-[#1C2530]/40">NO_MATCHES</div> : results.map((r) => (
                <div key={r.id} className="border-l-2 border-[#3E8F99] pl-2 py-1">
                  <div className="flex items-center gap-2"><span className="text-sm font-medium text-[#1C2530]">{r.title}</span><ConfBadge c={r.confidentiality} /></div>
                  <div className="text-xs text-[#1C2530]/70">{r.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="border border-[#DFDAD0] bg-white p-5" data-testid="kb-ingest-panel">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-[#B23A2E]" />
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Teach VIS from your business (Admin)</div>
            </div>
            <div className="text-xs text-[#1C2530]/50 mb-3">
              Ingest a website/social URL or a document (PDF, DOCX, PPTX). We extract the text and generate an AI summary.
              <span className="font-medium text-[#1C2530]/70"> Original files are not stored — only the extracted knowledge is kept.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <select value={ing.category} onChange={(e) => setIng({ ...ing, category: e.target.value })} data-testid="kb-ingest-category"
                className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={ing.confidentiality} onChange={(e) => setIng({ ...ing, confidentiality: e.target.value })} data-testid="kb-ingest-confidentiality"
                className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                <option value="public">public-safe</option>
                <option value="internal">internal-only</option>
              </select>
            </div>
            <div className="flex gap-2 mb-3">
              <div className="flex-1 flex items-center border border-[#DFDAD0] px-2 focus-within:border-[#1C2530]">
                <Link2 className="w-3.5 h-3.5 text-[#1C2530]/40" />
                <input value={ing.url} onChange={(e) => setIng({ ...ing, url: e.target.value })} disabled={ingBusy}
                  onKeyDown={(e) => e.key === "Enter" && ingestUrl()} data-testid="kb-ingest-url-input"
                  placeholder="https://yourcompany.com/about" className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent" />
              </div>
              <button onClick={ingestUrl} disabled={ingBusy} data-testid="kb-ingest-url-btn"
                className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-50">
                {ingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Learn
              </button>
            </div>
            <label data-testid="kb-ingest-file-label"
              className={`flex items-center justify-center gap-2 border border-dashed border-[#DFDAD0] py-3 text-xs font-mono uppercase tracking-wider text-[#1C2530]/60 ${ingBusy ? "opacity-50" : "cursor-pointer hover:border-[#1C2530] hover:text-[#1C2530]"}`}>
              {ingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
              Upload PDF / DOCX / PPTX
              <input type="file" accept=".pdf,.docx,.pptx" disabled={ingBusy} data-testid="kb-ingest-file-input" className="hidden"
                onChange={(e) => { ingestFile(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
        )}

        {isAdmin && (
          <div className="border border-[#DFDAD0] bg-white p-5" data-testid="kb-add-form">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Add Entry (Admin)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kb-title" placeholder="Title"
                className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="kb-category"
                className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.confidentiality} onChange={(e) => setForm({ ...form, confidentiality: e.target.value })} data-testid="kb-confidentiality"
                className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                <option value="public">public-safe</option>
                <option value="internal">internal-only</option>
              </select>
            </div>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="kb-content" placeholder="Content / knowledge chunk…"
              className="w-full border border-[#DFDAD0] px-2 py-1.5 text-sm h-20 outline-none focus:border-[#1C2530] mb-3" />
            <div className="flex gap-2">
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags, comma, separated"
                className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
              <button onClick={create} data-testid="kb-add-btn" className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
          </div>
        )}

        {!entries ? <Loading /> : entries.length === 0 ? <Empty label="NO_KB_ENTRIES" /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="kb-list">
            {entries.map((e) => (
              <div key={e.id} className="border border-[#DFDAD0] bg-white p-4" data-testid={`kb-entry-${e.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#1C2530]">{e.title}</span>
                    <span className="font-mono text-[9px] uppercase border border-[#DFDAD0] px-1 text-[#1C2530]/50">{e.category}</span>
                    {e.source_type && <span className="font-mono text-[9px] uppercase border border-[#B23A2E] text-[#B23A2E] px-1" data-testid={`kb-source-${e.id}`}>{e.source_type === "url" ? "url" : e.source_type}</span>}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      {e.source_type === "url" && (
                        <button onClick={() => refreshEntry(e.id)} disabled={refreshing === e.id} data-testid={`kb-refresh-${e.id}`}
                          title="Re-fetch this source" className="text-[#1C2530]/50 hover:text-[#1C2530] p-1 disabled:opacity-50">
                          <RefreshCw className={`w-3.5 h-3.5 ${refreshing === e.id ? "animate-spin" : ""}`} />
                        </button>
                      )}
                      <button onClick={() => del(e.id)} data-testid={`kb-del-${e.id}`} className="text-[#B23A2E] hover:bg-[#B23A2E]/10 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
                <KbText text={e.content} id={e.id} />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <ConfBadge c={e.confidentiality} />
                  {(e.tags || []).map((t) => <span key={t} className="font-mono text-[9px] text-[#1C2530]/40">#{t}</span>)}
                  {(e.updated_at || e.created_at) && (
                    <span className="ml-auto font-mono text-[9px] text-[#1C2530]/45" data-testid={`kb-updated-${e.id}`}>
                      {e.last_refreshed_at ? "Refreshed" : e.updated_at && e.updated_at !== e.created_at ? "Updated" : "Added"} {new Date(e.last_refreshed_at || e.updated_at || e.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const KB_PREVIEW = 240;
function KbText({ text, id }) {
  const [open, setOpen] = useState(false);
  const t = text || "";
  const long = t.length > KB_PREVIEW;
  return (
    <div className="mt-1">
      <div className={`text-xs text-[#1C2530]/70 ${open ? "whitespace-pre-wrap" : ""}`} data-testid={`kb-text-${id}`}>{open || !long ? t : `${t.slice(0, KB_PREVIEW).trimEnd()}…`}</div>
      {long && (
        <button onClick={() => setOpen(!open)} data-testid={`kb-toggle-${id}`} className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">
          {open ? "Collapse" : `Show full text · ${t.length.toLocaleString()} chars`}
        </button>
      )}
    </div>
  );
}
